import { h } from "preact";
import { useMemo, useRef, useEffect } from "preact/hooks";
import { marked } from "marked";
import type { ChatMessage } from "../types";
import { StreamingIndicator } from "./StreamingIndicator";
import { suggestCodeFilename } from "../../../../shared/util/codeFiles";
import { normalizeMath } from "../../../../shared/util/math";
import { loadMathRenderer } from "../api/mathLoader";

interface MessageBubbleProps {
  message: ChatMessage;
  isStreaming?: boolean;
}

// Configure marked for safe, compact output
marked.setOptions({
  breaks: true,
  gfm: true,
});

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * Math. normalizeMath (shared with the web chat) has already rewritten every span the
 * model wrote into two forms; these read exactly those. Each becomes a placeholder
 * holding its TeX as text — readable as-is while the renderer loads, or if it never
 * does — and the effect below swaps in MathML.
 */
marked.use({
  extensions: [
    {
      name: "mathBlock",
      level: "block",
      start: (src: string) => {
        const i = src.indexOf("$$\n");
        return i < 0 ? undefined : i;
      },
      tokenizer(src: string) {
        const m = /^\$\$\n([\s\S]+?)\n\$\$(?:\n+|$)/.exec(src);
        if (m) return { type: "mathBlock", raw: m[0], text: m[1] };
        return undefined;
      },
      renderer: (token: any) => `<div class="widget-math widget-math--display">${escapeHtml(token.text)}</div>\n`,
    },
    {
      name: "mathInline",
      level: "inline",
      start: (src: string) => {
        const i = src.indexOf("$$");
        return i < 0 ? undefined : i;
      },
      tokenizer(src: string) {
        const m = /^\$\$([^\n]+?)\$\$/.exec(src);
        if (m) return { type: "mathInline", raw: m[0], text: m[1] };
        return undefined;
      },
      renderer: (token: any) => `<span class="widget-math">${escapeHtml(token.text)}</span>`,
    },
  ],
});

/** Copy text to clipboard with a legacy fallback for non-secure contexts. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Save text as a file. The anchor goes on the host page's body, not into the widget's
 * shadow root: a click on a detached or shadow-hosted anchor is not honored everywhere.
 * Returns false only if building the file throws. A host page that forbids downloads
 * (an iframe sandboxed without allow-downloads) blocks the click silently, and the
 * page has no way to detect that — Copy is the fallback there.
 */
function downloadText(filename: string, text: string): boolean {
  try {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Not revoked synchronously: some browsers start the download asynchronously.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch {
    return false;
  }
}

/** marked writes the fence language as class="language-xxx" on the <code>. */
function codeLanguage(code: Element | null): string | null {
  const m = code?.className.match(/(?:^|\s)language-(\S+)/);
  return m ? m[1] : null;
}

export function MessageBubble({ message, isStreaming }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const isEmptyAssistant = !isUser && !message.content && isStreaming;
  const bubbleRef = useRef<HTMLDivElement>(null);

  const renderedHtml = useMemo(() => {
    if (isUser || !message.content) return null;
    return marked.parse(normalizeMath(message.content)) as string;
  }, [isUser, message.content]);

  // Swap math placeholders for MathML. The renderer is fetched on the first reply that
  // needs it; replies without math never trigger the request.
  useEffect(() => {
    const root = bubbleRef.current;
    if (!root || renderedHtml == null) return;
    const pending = Array.from(root.querySelectorAll<HTMLElement>(".widget-math:not([data-rendered])"));
    if (!pending.length) return;
    let cancelled = false;
    loadMathRenderer().then((render) => {
      if (!render || cancelled) return;
      for (const el of pending) {
        if (!el.isConnected) continue; // replaced by a later streamed chunk
        el.innerHTML = render(el.textContent ?? "", el.classList.contains("widget-math--display"));
        el.dataset.rendered = "1";
      }
    });
    return () => {
      cancelled = true;
    };
  }, [renderedHtml]);

  // Decorate each rendered code block with Copy and Download buttons. innerHTML is
  // replaced on every render, so this re-runs (keyed on renderedHtml) to re-attach.
  useEffect(() => {
    const root = bubbleRef.current;
    if (!root || renderedHtml == null) return;
    const blocks = root.querySelectorAll<HTMLPreElement>("pre");
    blocks.forEach((pre) => {
      if (pre.dataset.copyAdded) return;
      pre.dataset.copyAdded = "1";
      pre.classList.add("widget-code-block");
      const codeEl = pre.querySelector("code");
      const codeText = () => (codeEl?.innerText ?? pre.innerText).replace(/\n$/, "");

      const actions = document.createElement("div");
      actions.className = "widget-code-actions";

      const copyBtn = document.createElement("button");
      copyBtn.type = "button";
      copyBtn.className = "widget-copy-btn";
      copyBtn.textContent = "Copy";
      copyBtn.addEventListener("click", async (e) => {
        e.stopPropagation();
        const ok = await copyText(codeText());
        copyBtn.textContent = ok ? "Copied!" : "Failed";
        setTimeout(() => (copyBtn.textContent = "Copy"), 1500);
      });

      const downloadBtn = document.createElement("button");
      downloadBtn.type = "button";
      downloadBtn.className = "widget-copy-btn";
      downloadBtn.textContent = "Download";
      downloadBtn.title = "Save as a file";
      downloadBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        const text = codeText();
        const filename = suggestCodeFilename(codeLanguage(codeEl), text);
        const ok = downloadText(filename, text);
        downloadBtn.textContent = ok ? "Saved" : "Blocked";
        downloadBtn.title = ok ? filename : "This page does not allow downloads. Use Copy instead.";
        setTimeout(() => (downloadBtn.textContent = "Download"), 1500);
      });

      actions.append(copyBtn, downloadBtn);
      pre.appendChild(actions);
    });
  }, [renderedHtml]);

  return (
    <div class={`widget-message widget-message--${message.role}`}>
      <div class="widget-bubble">
        {isUser ? (
          message.content
        ) : isEmptyAssistant ? (
          <StreamingIndicator />
        ) : (
          <div ref={bubbleRef} dangerouslySetInnerHTML={{ __html: renderedHtml || "" }} />
        )}
      </div>
    </div>
  );
}
